import { Card } from "@rothoblaas/design-system";
import type { Translation } from "../../../../../hooks/useTranslations";
import type { Configuration, LockCalculatorDefinition } from "../../../../../http/lock_calculator/types";
import { buildVerificationCards } from "../../hooks/useLockCalculatorResults";
import { connectorCode } from "../../model/connectorCode";
import { formatKSer } from "./kSer";
import styles from "../../LockCalculator.module.css";
import type { LockCalculatorFormValues } from "../../model/form";

type Props = {
  calculation: LockCalculatorDefinition | null;
  comparisonConfigurations: Configuration[];
  onRemoveComparison: (id: string) => void;
  loads: LockCalculatorFormValues["loads"] | undefined;
  hasActiveCombination: boolean;
  readOnly: boolean;
  onOpenHelp: (text: string, image?: string) => void;
  t: Translation;
};

const combinedLabel = (activeCombination: string | null | undefined, t: Translation) => {
  const labels: Record<string, string> = {
    vertical: t.lockCalculatorModule.results.axes.vertical,
    uplift: t.lockCalculatorModule.results.axes.uplift,
    axial: t.lockCalculatorModule.results.axes.axial,
    lateral: t.lockCalculatorModule.results.axes.lateral,
  };
  return activeCombination
    ? activeCombination
        .split("+")
        .map((key) => labels[key] ?? key)
        .join(" + ")
    : t.lockCalculatorModule.results.utilizationRatio;
};

const configurationLabel = (configuration: Configuration): string =>
  `${configurationConnectorLabel(configuration)} ${configurationHeaderFastener(configuration)} ${configurationJoistFastener(configuration)} ${configurationInclinedFastener(configuration)} ${configurationLockstop(configuration)}`;

const configurationConnectorLabel = (configuration: Configuration): string => {
  const codes = (configuration.connectors ?? []).map(connectorCode).filter((code): code is string => !!code);
  if (codes.length > 0) return codes.join(" + ");
  return configuration.headerFastenerItem?.item.code || "—";
};

const configurationHeaderFastener = (configuration: Configuration): string =>
  configuration.headerFastenerItem?.item.code || "";

const configurationJoistFastener = (configuration: Configuration): string =>
  configuration.joistFastenerItem?.item.code || "";

const configurationInclinedFastener = (configuration: Configuration): string =>
  configuration.inclinedFastenerItem?.item.code || "";

const configurationLockstop = (configuration: Configuration): string => configuration.lockstopItem?.item.code || "";

export const ResultsSection = ({
  calculation,
  comparisonConfigurations,
  onRemoveComparison,
  loads,
  hasActiveCombination,
  t,
}: Props) => {
  const finalConfiguration = calculation?.configuration;

  return (
    <section
      className={styles.resultsSection}
      aria-label={t.lockCalculatorModule.aria.calculationResults}
    >
      {finalConfiguration && (
        <ConfigurationResultBlock
          label={t.lockCalculatorModule.results.finalConfiguration}
          result={finalConfiguration.result}
          loads={loads}
          hasActiveCombination={hasActiveCombination}
          t={t}
        />
      )}
      {comparisonConfigurations.map((configuration) => (
        <ConfigurationResultBlock
          key={configuration.id}
          label={configurationLabel(configuration)}
          result={configuration.result}
          loads={loads}
          hasActiveCombination={hasActiveCombination}
          onRemove={() => onRemoveComparison(configuration.id)}
          t={t}
        />
      ))}
    </section>
  );
};

type ConfigurationResultBlockProps = {
  label: string;
  result: Configuration["result"];
  loads: LockCalculatorFormValues["loads"] | undefined;
  hasActiveCombination: boolean;
  onRemove?: () => void;
  t: Translation;
};

const ConfigurationResultBlock = ({
  label,
  result,
  loads,
  hasActiveCombination,
  onRemove,
  t,
}: ConfigurationResultBlockProps) => {
  const verificationCards = buildVerificationCards(loads, result, t);
  return (
    <div className={styles.contentGrid}>
      <div className={styles.resultsBlockHeader}>
        <span className={`${styles.span12} ${styles.resultsBlockLabel}`}>{label}</span>
        {onRemove && (
          <button
            type="button"
            className={styles.resultsBlockRemove}
            aria-label={t.lockCalculatorModule.results.removeComparison}
            onClick={onRemove}
          >
            ×
          </button>
        )}
      </div>
      <Card style={{ minHeight: 400 }}
        shadow
        title={t.lockCalculatorModule.results.title}
        className={`${styles.span12} ${styles.compactCard}`}
      >
        <div className={styles.resultsRow}>
          {verificationCards.map((card) => {
            const resistanceKN = card.resistance != null ? card.resistance / 1000 : null;
            const eta =
              card.load != null && card.load > 0 && resistanceKN != null && resistanceKN > 0
                ? card.load / resistanceKN
                : null;
            const isPassed = eta != null && eta < 1;
            const isFailed = eta != null && eta >= 1;
            const statusClass = isPassed ? styles.resultsMiniCardPassed : isFailed ? styles.resultsMiniCardFailed : "";
            return (
              <div key={card.title} className={`${styles.resultsMiniCard} ${statusClass}`}>
                <div className={styles.resultsMiniCardHeader}>
                  <span className={styles.resultsMiniCardLabel}>
                    {card.symbol} {card.title}
                  </span>
                  {eta != null && (
                    <span className={`${styles.statusBadge} ${isPassed ? styles.statusOk : styles.statusFailed}`}>
                      {isPassed ? t.lockCalculatorModule.results.verified : t.lockCalculatorModule.results.failed}
                    </span>
                  )}
                  {eta == null && card.load != null && card.load > 0 && (
                    <span className={`${styles.statusBadge} ${styles.statusPending}`}>
                      {t.lockCalculatorModule.results.noLoad}
                    </span>
                  )}
                </div>
                <ResultRow
                  label={t.lockCalculatorModule.results.load}
                  value={card.load != null ? card.load.toFixed(2) : "—"}
                  unit="kN"
                />
                <ResultRow
                  label={t.lockCalculatorModule.results.resistance}
                  value={resistanceKN != null ? resistanceKN.toFixed(2) : "—"}
                  unit="kN"
                />
                <ResultRow
                  label={t.lockCalculatorModule.results.eta}
                  value={eta != null ? `${(eta * 100).toFixed(1)}%` : "—"}
                />
              </div>
            );
          })}
        </div>
      </Card>
      <Card style={{ minHeight: 400 }}
        shadow
        title={t.lockCalculatorModule.combined.title}
        className={`${styles.span12} ${styles.compactCard}`}
      >
        {(() => {
          if (result?.etaCombined == null || !hasActiveCombination) {
            return (
              <div className={styles.resultsMiniCard}>
                <ResultRow label={t.lockCalculatorModule.results.utilizationRatio} value="—" />
              </div>
            );
          }
          return (
            <div
              className={`${styles.resultsMiniCard} ${result.isVerified ? styles.resultsMiniCardPassed : styles.resultsMiniCardFailed}`}
            >
              <div className={styles.resultsMiniCardHeader}>
                <span className={styles.resultsMiniCardLabel}>{combinedLabel(result.activeCombination, t)}</span>
                <span className={`${styles.statusBadge} ${result.isVerified ? styles.statusOk : styles.statusFailed}`}>
                  {result.isVerified ? t.lockCalculatorModule.results.verified : t.lockCalculatorModule.results.failed}
                </span>
              </div>
              <ResultRow
                label={t.lockCalculatorModule.results.utilizationRatio}
                value={`${(result.etaCombined * 100).toFixed(1)}%`}
              />
            </div>
          );
        })()}
      </Card>
      {/* Mirrors computeVerticalDown's K_ser gate (eta_190831_lock.go) — only
          shown with a real (positive) lateral load, same as the backend calc. */}
      {loads?.lateral != null && loads.lateral > 0 && (
        <Card style={{ minHeight: 400 }}
          shadow
          title={t.lockCalculatorModule.results.slipModulus}
          className={`${styles.span12} ${styles.compactCard}`}
        >
          <div className={styles.resultsMiniCard}>
            <ResultRow
              label={t.lockCalculatorModule.results.slipModulus}
              value={formatKSer(result?.k23Ser)?.value ?? "—"}
              unit={formatKSer(result?.k23Ser)?.unit}
            />
          </div>
        </Card>
      )}
    </div>
  );
};

const ResultRow = ({ label, value, unit }: { label: string; value: string; unit?: string }) => (
  <div className={styles.resultsMiniCardRow}>
    <span className={styles.resultsMiniCardSub}>{label}</span>
    <span className={styles.resultsMiniCardValue}>
      {value}
      {unit && <span className={styles.resultsMiniCardUnit}> {unit}</span>}
    </span>
  </div>
);
